/**
 * MSAL State Cleanup Utilities
 * 
 * These utilities help resolve MSAL authentication issues, particularly
 * the "interaction_in_progress" error that can occur when switching between
 * environments or after incomplete login attempts.
 */

import { IPublicClientApplication } from '@azure/msal-browser';
import { getCurrentEnvironment } from './environment';

/**
 * Clear all MSAL-related localStorage items that might be causing interaction conflicts
 */
export function clearMsalStorage(): void {
  const currentEnv = getCurrentEnvironment();

  const msalKeys: string[] = [];
  const interactionKeys: string[] = [];
  const allKeysToRemove: string[] = [];

  // Collect ALL keys from localStorage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      // MSAL v3 uses keys starting with 'msal.'
      if (key.startsWith('msal.')) {
        msalKeys.push(key);
        allKeysToRemove.push(key);
      }

      // Interaction-specific keys (more comprehensive patterns)
      if (key.includes('.interaction.') ||
          key.includes('interaction_in_progress') ||
          key.includes('interaction_status') ||
          key.includes('interaction_type') ||
          key.includes('server.telemetry') ||
          key.includes('request.params') ||
          key.includes('request.origin') ||
          key.includes('request.state') ||
          key.includes('authority') ||
          key.includes('.token.') ||
          key.includes('.account.') ||
          key.includes('.nonce.') ||
          key.includes('.state.') ||
          key.includes('acquireToken') ||
          key.includes('loginRedirect') ||
          key.includes('loginPopup')) {
        if (!allKeysToRemove.includes(key)) {
          interactionKeys.push(key);
          allKeysToRemove.push(key);
        }
      }
    }
  }

  // Remove all collected keys
  allKeysToRemove.forEach(key => {
    try {
      const value = localStorage.getItem(key);
      localStorage.removeItem(key);
      console.log(`Cleared key: ${key}`, value ? `(had value: ${value.substring(0, 50)}...)` : '(was empty)');
    } catch (e) {
      console.error(`Failed to clear key ${key}:`, e);
    }
  });

  // Also clear sessionStorage items
  const sessionKeysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (key.startsWith('msal.') || key.includes('.interaction.'))) {
      sessionKeysToRemove.push(key);
    }
  }

  sessionKeysToRemove.forEach(key => {
    sessionStorage.removeItem(key);
    console.log(`Cleared sessionStorage key: ${key}`);
  });

  console.log(`Cleared ${msalKeys.length} MSAL keys, ${interactionKeys.length} interaction keys, and ${sessionKeysToRemove.length} session keys for ${currentEnv.displayName}`);
}

/**
 * Force clear MSAL interaction state by directly manipulating the cache
 */
export function forceEndMsalInteraction(msalInstance?: IPublicClientApplication): void {
  try {
    const currentEnv = getCurrentEnvironment();
    console.log(`Force-clearing MSAL interaction state for ${currentEnv.displayName}...`);

    // If we have access to the MSAL instance, try to clear its internal state
    if (msalInstance) {
      try {
        // Try multiple approaches to access MSAL's internal state
        const possiblePaths = [
          (msalInstance as any).browserStorage,
          (msalInstance as any).controller?.browserStorage,
          (msalInstance as any).controller?.cacheManager,
          (msalInstance as any).browserCrypto,
          (msalInstance as any).tokenCache
        ];

        for (const storage of possiblePaths) {
          if (!storage) continue;

          console.log('Found storage object:', storage.constructor?.name || 'unknown');

          // Try different methods to clear interaction state
          const clearMethods = [
            'setInteractionInProgress',
            'clearInteractionInProgress',
            'removeValue',
            'clear',
            'clearTempCache',
            'clearMsalCookies'
          ];

          for (const method of clearMethods) {
            if (typeof storage[method] === 'function') {
              try {
                if (method === 'setInteractionInProgress') {
                  storage[method](false);
                  console.log(`Cleared interaction via ${method}(false)`);
                } else if (method === 'removeValue') {
                  // Try to remove specific interaction keys
                  const interactionKeys = [
                    'interaction.status',
                    'interaction.in.progress'
                  ];
                  for (const key of interactionKeys) {
                    try {
                      storage[method](key);
                      console.log(`Removed key ${key} via ${method}`);
                    } catch (e) {
                      // Silent fail for individual keys
                    }
                  }
                } else {
                  storage[method]();
                  console.log(`Cleared via ${method}()`);
                }
              } catch (e) {
                console.warn(`Failed to call ${method}:`, e);
              }
            }
          }

          // Try direct property access
          if (storage.interactionInProgress !== undefined) {
            storage.interactionInProgress = false;
            console.log('Directly set interactionInProgress to false');
          }
        }
      } catch (error) {
        console.warn('Could not access MSAL internal storage:', error);
      }
    }

    // Manually clear all possible MSAL interaction keys
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes('.interaction.') ||
        key.includes('interaction_in_progress') ||
        key.includes('interaction_status') ||
        key.includes('interaction_type') ||
        key.includes('request.params') ||
        key.includes('request.origin') ||
        key.includes('request.state')
      )) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`Manually removed interaction key: ${key}`);
    });

    console.log('Force-clear interaction state completed');
  } catch (error) {
    console.error('Error force-clearing MSAL interaction state:', error);
  }
}

/**
 * Check if MSAL interaction is stuck and provide diagnostic info
 */
export function diagnoseMsalState(): { hasStuckInteraction: boolean; stuckKeys: string[]; allMsalKeys: string[] } {
  const stuckKeys: string[] = [];
  const allMsalKeys: string[] = [];

  // Log all localStorage keys for debugging
  console.log('Total localStorage keys:', localStorage.length);

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      // Track all MSAL-related keys
      if (key.startsWith('msal.') || key.includes('msal')) {
        allMsalKeys.push(key);
      }

      // Check for interaction keys
      if (key.includes('.interaction.') ||
          key.includes('interaction_in_progress') ||
          key.includes('interaction_status') ||
          key.includes('interaction_type')) {
        stuckKeys.push(key);
      }
    }
  }

  // Also check sessionStorage
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (key.startsWith('msal.') || key.includes('.interaction.'))) {
      console.log('Found MSAL key in sessionStorage:', key);
    }
  }

  const hasStuckInteraction = stuckKeys.length > 0;

  if (allMsalKeys.length > 0) {
    console.log('All MSAL keys found:', allMsalKeys);
  }

  if (hasStuckInteraction) {
    console.log('MSAL stuck interaction detected:', stuckKeys);
  }

  return { hasStuckInteraction, stuckKeys, allMsalKeys };
}

/**
 * Clear all cookies for current domain
 */
function clearAllCookies(): void {
  console.log('Clearing all cookies...');

  // Get all cookies
  const cookies = document.cookie.split(';');

  // Clear each cookie
  cookies.forEach(cookie => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
    if (name) {
      // Clear for current path
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
      // Clear for current domain
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${window.location.hostname}`;
      // Clear for parent domain
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=.${window.location.hostname}`;
      // Clear for localhost specifically
      if (window.location.hostname === 'localhost') {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=localhost`;
      }
      console.log(`Cleared cookie: ${name}`);
    }
  });
}

/**
 * Complete cleanup for switching environments or resolving login issues
 */
export function performCompleteMsalCleanup(msalInstance?: IPublicClientApplication): Promise<void> {
  return new Promise((resolve) => {
    const currentEnv = getCurrentEnvironment();
    console.log(`Performing complete MSAL cleanup for ${currentEnv.displayName}...`);

    // Step 1: Try to clear MSAL cache through the instance
    if (msalInstance) {
      try {
        // Clear all accounts from MSAL cache
        const accounts = msalInstance.getAllAccounts();
        console.log(`Found ${accounts.length} accounts in cache`);

        // Try to logout all accounts
        accounts.forEach(account => {
          try {
            // In MSAL v3, we can't directly remove accounts, but we can try to clear their data
            console.log(`Account found: ${account.username}`);
          } catch (e) {
            console.warn('Error processing account:', e);
          }
        });

        // Try to reset the instance's internal state
        const controller = (msalInstance as any).controller;
        if (controller) {
          // Try to clear interaction in progress flag directly
          if (controller.isBrowserEnvironment && controller.browserStorage) {
            try {
              controller.browserStorage.setInteractionInProgress(false);
              console.log('Reset interaction flag via controller');
            } catch (e) {
              console.warn('Could not reset via controller:', e);
            }
          }
        }

        // Try to access browserStorage directly
        const browserStorage = (msalInstance as any).browserStorage;
        if (browserStorage) {
          try {
            // MSAL v3 specific method
            browserStorage.setInteractionInProgress(false);
            console.log('Reset interaction flag via browserStorage');
          } catch (e) {
            console.warn('Could not reset via browserStorage:', e);
          }
        }
      } catch (error) {
        console.warn('Error clearing MSAL instance cache:', error);
      }
    }

    // Step 2: Force end any interactions
    forceEndMsalInteraction(msalInstance);

    // Step 3: Clear all MSAL storage
    clearMsalStorage();

    // Step 4: Clear ALL cookies (more aggressive)
    clearAllCookies();

    // Step 5: Small delay to ensure cleanup is processed
    setTimeout(() => {
      console.log(`Complete MSAL cleanup finished for ${currentEnv.displayName}`);
      resolve();
    }, 500);
  });
}